import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Trash2, GripVertical, Settings2,
  Type, Hash, Mail, Phone, Calendar, Clock, List, CheckSquare, ToggleLeft,
  Upload, PenTool, MapPin, Star, Minus, FileText, Users,
} from 'lucide-react';

const FIELD_TYPES = [
  { value: 'short_text', label: 'Short Text', icon: Type },
  { value: 'long_text', label: 'Long Text', icon: FileText },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'time', label: 'Time', icon: Clock },
  { value: 'dropdown', label: 'Dropdown', icon: List },
  { value: 'multi_select', label: 'Multi Select', icon: CheckSquare },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  { value: 'radio', label: 'Radio', icon: ToggleLeft },
  { value: 'yes_no', label: 'Yes/No', icon: ToggleLeft },
  { value: 'file_upload', label: 'File Upload', icon: Upload },
  { value: 'signature', label: 'Signature', icon: PenTool },
  { value: 'address', label: 'Address', icon: MapPin },
  { value: 'rating', label: 'Rating', icon: Star },
  { value: 'section_header', label: 'Section', icon: Minus },
  { value: 'divider', label: 'Divider', icon: Minus },
  { value: 'participant_lookup', label: 'Participant', icon: Users },
  { value: 'staff_lookup', label: 'Staff', icon: Users },
];

const MODULE_TYPES = [
  'GENERAL', 'NDIS_ASSESSMENT', 'INCIDENT_REPORT', 'ONBOARDING',
  'STAFF_COMPLIANCE', 'SHIFT_CHECKLIST', 'SIL_AUDIT', 'RECRUITMENT', 'PARTICIPANT_INTAKE',
];

let fieldIdCounter = 0;
function genFieldId() { return `field_${Date.now()}_${++fieldIdCounter}`; }

function makeField(type) {
  const ft = FIELD_TYPES.find(f => f.value === type);
  return {
    _uid: genFieldId(), type, label: ft?.label || type,
    key: '', required: false, placeholder: '', help_text: '', options: [],
  };
}

function FieldCard({ field, index, onUpdate, onRemove }) {
  const [open, setOpen] = useState(false);
  const ft = FIELD_TYPES.find(f => f.value === field.type);
  const Icon = ft?.icon || Type;
  const hasOptions = ['dropdown', 'multi_select', 'radio', 'checkbox'].includes(field.type);

  return (
    <Draggable draggableId={field._uid} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`rounded-lg border bg-card transition-shadow ${snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/40' : 'shadow-sm'}`}
          data-testid={`field-editor-${index}`}
        >
          <div className="flex items-center gap-2 px-3 py-2.5">
            <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing p-0.5" data-testid={`drag-handle-${index}`}>
              <GripVertical className="w-4 h-4 text-muted-foreground" />
            </div>
            <Icon className="w-4 h-4 text-primary/70 shrink-0" />
            <span className="text-sm font-medium flex-1 truncate">{field.label || 'Untitled'}</span>
            <Badge variant="outline" className="text-[10px] px-1.5">{ft?.label || field.type}</Badge>
            {field.required && <Badge variant="destructive" className="text-[10px] px-1.5">Req</Badge>}
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpen(!open)} data-testid={`toggle-field-${index}`}>
              <Settings2 className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={onRemove} data-testid={`remove-field-${index}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>

          {open && (
            <div className="px-3 pb-3 border-t">
              <div className="grid grid-cols-2 gap-3 pt-3">
                <div>
                  <Label className="text-xs">Label</Label>
                  <Input value={field.label || ''} onChange={e => onUpdate({ ...field, label: e.target.value })} className="h-8 text-sm" data-testid={`field-label-${index}`} />
                </div>
                <div>
                  <Label className="text-xs">Field Key</Label>
                  <Input value={field.key || ''} onChange={e => onUpdate({ ...field, key: e.target.value })} className="h-8 text-sm" placeholder="auto_generated" data-testid={`field-key-${index}`} />
                </div>
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={field.type} onValueChange={v => onUpdate({ ...field, type: v })}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{FIELD_TYPES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Placeholder</Label>
                  <Input value={field.placeholder || ''} onChange={e => onUpdate({ ...field, placeholder: e.target.value })} className="h-8 text-sm" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Help Text</Label>
                  <Input value={field.help_text || ''} onChange={e => onUpdate({ ...field, help_text: e.target.value })} className="h-8 text-sm" />
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={field.required || false} onCheckedChange={v => onUpdate({ ...field, required: v })} />
                  <Label className="text-xs">Required</Label>
                </div>
                {hasOptions && (
                  <div className="col-span-2">
                    <Label className="text-xs">Options (one per line)</Label>
                    <Textarea rows={3} value={(field.options || []).join('\n')} onChange={e => onUpdate({ ...field, options: e.target.value.split('\n').filter(Boolean) })} className="text-sm" />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
}

export default function FormTemplateBuilderPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const isEdit = templateId && templateId !== 'new';
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '', description: '', module_type: 'GENERAL', category_id: '', schema_json: [],
  });

  useEffect(() => {
    if (isEdit) {
      api.get(`/forms/templates/${templateId}`).then(({ data }) => {
        const t = data.data;
        const fields = (t.version?.schema_json || []).map(f => ({ ...f, _uid: genFieldId() }));
        setForm({
          name: t.name || '', description: t.description || '',
          module_type: t.module_type || 'GENERAL', category_id: t.category_id || '',
          schema_json: fields,
        });
      }).catch(() => toast.error('Failed to load template'));
    }
  }, [isEdit, templateId]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, description: form.description, module_type: form.module_type,
        category_id: form.category_id || null,
        schema_json: form.schema_json.map((f, i) => {
          const { _uid, ...rest } = f;
          return { ...rest, key: rest.key || `field_${i + 1}`, order: i };
        }),
      };
      if (isEdit) {
        await api.put(`/forms/templates/${templateId}`, payload);
        toast.success('Template updated');
      } else {
        const { data } = await api.post('/forms/templates', payload);
        toast.success('Template created');
        navigate(`/forms/templates/${data.data.id}/edit`, { replace: true });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    // Dropping from palette onto canvas
    if (source.droppableId === 'palette' && destination.droppableId === 'canvas') {
      const fieldType = FIELD_TYPES[source.index]?.value;
      if (!fieldType) return;
      const newField = makeField(fieldType);
      setForm(prev => {
        const arr = [...prev.schema_json];
        arr.splice(destination.index, 0, newField);
        return { ...prev, schema_json: arr };
      });
      return;
    }

    // Reordering within canvas
    if (source.droppableId === 'canvas' && destination.droppableId === 'canvas') {
      if (source.index === destination.index) return;
      setForm(prev => {
        const arr = [...prev.schema_json];
        const [moved] = arr.splice(source.index, 1);
        arr.splice(destination.index, 0, moved);
        return { ...prev, schema_json: arr };
      });
    }
  };

  const removeField = (idx) => {
    setForm(prev => ({ ...prev, schema_json: prev.schema_json.filter((_, i) => i !== idx) }));
  };

  const updateField = (idx, updated) => {
    setForm(prev => ({ ...prev, schema_json: prev.schema_json.map((f, i) => i === idx ? updated : f) }));
  };

  return (
    <div className="space-y-5" data-testid="form-template-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/forms/templates')} data-testid="back-to-templates">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{isEdit ? 'Edit Template' : 'New Template'}</h1>
            <p className="text-muted-foreground text-sm">Drag fields from the palette into the canvas</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Template'}
        </Button>
      </div>

      {/* Template Metadata */}
      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Incident Report Form" data-testid="template-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description..." data-testid="template-description-input" />
            </div>
            <div>
              <Label>Module Type</Label>
              <Select value={form.module_type} onValueChange={v => setForm(p => ({ ...p, module_type: v }))}>
                <SelectTrigger data-testid="module-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MODULE_TYPES.map(m => <SelectItem key={m} value={m}>{m.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* DnD Builder */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">

          {/* ── Field Palette ── */}
          <div>
            <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wider">Field Palette</h3>
            <Droppable droppableId="palette" isDropDisabled={true}>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-1.5" data-testid="field-palette">
                  {FIELD_TYPES.map((ft, index) => (
                    <Draggable key={ft.value} draggableId={`palette-${ft.value}`} index={index}>
                      {(provided, snapshot) => {
                        const Icon = ft.icon;
                        return (
                          <>
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`flex items-center gap-2.5 px-3 py-2 rounded-md border cursor-grab active:cursor-grabbing select-none transition-colors
                                ${snapshot.isDragging ? 'bg-primary/10 border-primary shadow-md' : 'bg-card hover:bg-muted/50 border-border'}`}
                              data-testid={`palette-${ft.value}`}
                            >
                              <GripVertical className="w-3.5 h-3.5 text-muted-foreground/50" />
                              <Icon className="w-4 h-4 text-primary/70" />
                              <span className="text-sm">{ft.label}</span>
                            </div>
                            {snapshot.isDragging && (
                              <div className="flex items-center gap-2.5 px-3 py-2 rounded-md border bg-card border-border opacity-50">
                                <GripVertical className="w-3.5 h-3.5 text-muted-foreground/50" />
                                <Icon className="w-4 h-4 text-primary/70" />
                                <span className="text-sm">{ft.label}</span>
                              </div>
                            )}
                          </>
                        );
                      }}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>

          {/* ── Form Canvas ── */}
          <div>
            <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wider">
              Form Canvas ({form.schema_json.length} fields)
            </h3>
            <Droppable droppableId="canvas">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`min-h-[300px] rounded-lg border-2 border-dashed p-3 transition-colors space-y-2
                    ${snapshot.isDraggingOver ? 'border-primary/50 bg-primary/5' : 'border-border bg-muted/20'}`}
                  data-testid="form-canvas"
                >
                  {form.schema_json.length === 0 && !snapshot.isDraggingOver && (
                    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                      <GripVertical className="w-10 h-10 mb-3 opacity-30" />
                      <p className="font-medium">Drop fields here</p>
                      <p className="text-sm mt-1">Drag from the palette on the left</p>
                    </div>
                  )}
                  {form.schema_json.map((field, idx) => (
                    <FieldCard
                      key={field._uid}
                      field={field}
                      index={idx}
                      onUpdate={(updated) => updateField(idx, updated)}
                      onRemove={() => removeField(idx)}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </div>
      </DragDropContext>
    </div>
  );
}
